import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { AuthService } from '@app-core/services';
import { ConfigService } from '@app-core/services';
import * as fromRoot from '@app-store/reducers';
import * as UserActions from '@app-store/user/user.actions';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RequestInterceptor implements HttpInterceptor {
  private _configService = inject(ConfigService);
  private _authService = inject(AuthService);
  private _store$ = inject<Store<fromRoot.State>>(Store);

  private previousUrl: string = '';
  private apiCallsToIgnoreLoading: string[] = [
    '/refreshtoken',
    '/getclientinfo',
    '/getcaptcha',
    '/verifyusername',
    '/contractactivity',
    '/refreshtoken',
  ];
  private apiCallsToIgnoreHidingError: string[] = ['/refreshtoken', '/manualdecode', '/getcaptcha'];
  // CON-966: these failures route to conflict/SSO warning — never flash the red top alert.
  private apiCallsToIgnoreDisplayingError: string[] = ['/okta/getOktaUser', '/ssologin'];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let byPassLoading = false;
    this.apiCallsToIgnoreLoading.forEach((url) => (byPassLoading ||= request.url.includes(url)));

    // Bypass loading bar when API calls are made for refresh token
    if (!byPassLoading) {
      this._configService.requestInterceptor();
    }

    let token = '';

    // SSO flow
    if (request.url.endsWith('/ssologin')) {
      const ssoToken = this._authService.getCookie();
      token = `Bearer ${ssoToken}`;
    } else {
      if (localStorage.getItem('token')) {
        token = `Bearer ${localStorage.getItem('token')}`;
      }
    }

    let langCode = '';
    if (localStorage.getItem('languageCode')) {
      langCode = localStorage.getItem('languageCode');
    }

    let callToken = token;
    if (request.headers.has('TokenType')) {
      callToken = request.headers.get('Authorization');
    }

    // removing withCredentials since app is not passing cookies back to server.
    const authReq = request.clone({
      headers: new HttpHeaders({ Authorization: callToken, 'Accept-Language': langCode }),
    });

    return next.handle(authReq).pipe(
      tap((response: HttpResponse<any>) => {
        if (response.status === 200) {
          let noByPass = false;

          this.apiCallsToIgnoreHidingError.forEach((url) => {
            if (request.url.endsWith(url)) {
              noByPass = true;
            }
          });
          if (!noByPass) {
            this._configService.hideErrorMessage();
          }
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // Network Error - When service is down
        this.previousUrl = request.url;

        if (err.status === 403) {
          this._store$.dispatch(new UserActions.LogOutAction({ davinciLogout: true, oktaLogout: false }));
        }

        if (err.status === 0) {
          err = new HttpErrorResponse({
            status: 100,
            statusText: 'Network Error - service may be down, please try again later.',
          });
        }

        const skipTopAlert = this.apiCallsToIgnoreDisplayingError.some((url) => request.url.includes(url));
        if (!skipTopAlert) {
          this._configService.displayErrorMessage(err.status, err.statusText);
        }

        return throwError(() => err);
      }),
      finalize(() => this._configService.responseInterceptor())
    );
  }
}

// use request interceptor to intercept and add the token to the header of each request
export const requestInterceptor = {
  provide: HTTP_INTERCEPTORS,
  useClass: RequestInterceptor,
  multi: true,
};
